import { useCallback, useEffect, useRef } from 'react';
import { Image } from 'lucide-react';
import { useGooglePhotos, type GooglePhotosStatus } from '../../hooks/useGooglePhotos';

interface GooglePhotosPickerProps {
  /**
   * Handed each photograph as it finishes downloading. May return a promise,
   * which the import waits on — that is what keeps a large selection from
   * piling up in memory faster than it can be uploaded.
   */
  onFilesSelected: (files: File[]) => void | Promise<void>;
  disabled?: boolean;
  /**
   * Awaited before the picker opens. Resolving `false` aborts the import —
   * this is how a caller inserts a step (e.g. choosing tags for the whole
   * import) before any file is picked, since files then stream in one at a
   * time and there is no later point that covers the whole batch.
   */
  onBeforeImport?: () => Promise<boolean>;
}

const STATUS_LABELS: Record<GooglePhotosStatus, string> = {
  idle: 'Google Photos',
  'loading-sdk': 'Loading...',
  authorizing: 'Authorizing...',
  picking: 'Picking...',
  importing: 'Importing...',
  done: 'Google Photos',
  error: 'Google Photos',
};

export function GooglePhotosPicker({
  onFilesSelected,
  disabled,
  onBeforeImport,
}: GooglePhotosPickerProps) {
  const { importPhotos, cancel, status, error, pickerUrl } = useGooglePhotos();

  // The import outlives the render that started it (it awaits the modal, then
  // streams for minutes), so it must call the latest handler, not the one
  // captured when the button was pressed.
  const onFilesSelectedRef = useRef(onFilesSelected);
  useEffect(() => {
    onFilesSelectedRef.current = onFilesSelected;
  }, [onFilesSelected]);

  const busy = status !== 'idle' && status !== 'done' && status !== 'error';

  const handleClick = useCallback(async () => {
    if (busy) {
      cancel();
      return;
    }
    if (onBeforeImport) {
      const proceed = await onBeforeImport();
      if (!proceed) return;
    }
    // Stream each file to the upload queue as it's downloaded, awaiting the
    // handler so a full queue slows the download rather than outrunning it.
    await importPhotos((file) => onFilesSelectedRef.current([file]));
  }, [busy, cancel, importPhotos, onBeforeImport]);

  // Don't render if VITE_GOOGLE_CLIENT_ID is not configured
  if (!import.meta.env.VITE_GOOGLE_CLIENT_ID) return null;

  return (
    <div>
      <button
        onClick={handleClick}
        disabled={disabled || (busy && status !== 'picking')}
        className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <Image size={16} />
        {busy ? STATUS_LABELS[status] : 'Google Photos'}
      </button>
      {status === 'picking' && pickerUrl && (
        <p className="mt-1 text-xs text-gray-600">
          Google Photos didn&apos;t open, or stuck loading?{' '}
          <a
            href={pickerUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-primary-600 underline hover:no-underline"
          >
            Open the picker
          </a>
          . Press the button again to cancel.
        </p>
      )}
      {error && <p className="text-sm text-red-600 mt-1">{error}</p>}
    </div>
  );
}
